import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import CafeQR from "../components/CafeQR";
import { generateTableTentPDF } from "../lib/tableTentPDF";

const STORAGE_KEY = "soma_admin_key";

/**
 * Admin tool for the sales person to:
 *   1. Create cafe pages on the spot during in-person conversations
 *   2. See every cafe that's been onboarded
 *
 * Auth: same X-Admin-Key pattern as /admin/feedback.
 * The temp password generated server-side is shown ONCE in a copyable card —
 * sales person reads it back to the partner. Partner can change after login.
 */
export default function AdminCafesPage() {
  const [authed, setAuthed] = useState(null); // null=checking, false=needs key, true=ok
  const [keyInput, setKeyInput] = useState("");
  const [error, setError] = useState(null);
  const [adminKey, setAdminKey] = useState("");
  const [cafes, setCafes] = useState([]);

  // create form
  const [form, setForm] = useState({
    name: "",
    slug: "",
    email: "",
    custom_note: "",
    payout_method: "venmo",
    payout_handle: "",
    partner_type: "cafe",
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [justCreated, setJustCreated] = useState(null);

  // Table-tent picker (resets each time a new cafe is created)
  const [pdfVariant, setPdfVariant] = useState("classic");
  const [pdfLight, setPdfLight] = useState(true);

  // Delete-confirm modal
  const [deleting, setDeleting] = useState(null);          // { cafe, footprint } | null
  const [deleteTyped, setDeleteTyped] = useState("");
  const [deleteWorking, setDeleteWorking] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // /begin recruitment funnel stats
  const [beginStats, setBeginStats] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) { setAuthed(false); return; }
    setKeyInput(saved);
    tryAuth(saved);
  }, []);

  const loadBeginStats = async (k) => {
    try {
      const res = await axios.get(`${API}/admin/begin-stats`, { headers: { "X-Admin-Key": k } });
      setBeginStats(res.data);
    } catch (_e) { /* ignore */ }
  };

  const tryAuth = async (k) => {
    try {
      const res = await axios.get(`${API}/admin/cafes`, { headers: { "X-Admin-Key": k } });
      localStorage.setItem(STORAGE_KEY, k);
      setAdminKey(k);
      setCafes(res.data.cafes || []);
      setAuthed(true);
      setError(null);
      loadBeginStats(k);
    } catch (err) {
      localStorage.removeItem(STORAGE_KEY);
      setAuthed(false);
      setError(err.response?.status === 401 ? "That key doesn't match." : "Something went wrong.");
    }
  };

  const reload = async () => {
    try {
      const res = await axios.get(`${API}/admin/cafes`, { headers: { "X-Admin-Key": adminKey } });
      setCafes(res.data.cafes || []);
    } catch (_e) { /* ignore */ }
  };

  const openDelete = async (cafe) => {
    setDeleteError(null);
    setDeleteTyped("");
    try {
      const res = await axios.get(`${API}/admin/cafes/${cafe.slug}/footprint`, { headers: { "X-Admin-Key": adminKey } });
      setDeleting({ cafe, footprint: res.data });
    } catch (err) {
      setDeleteError(err.response?.data?.detail || err.message || "Could not load footprint");
      setDeleting({ cafe, footprint: null });
    }
  };

  const confirmDelete = async () => {
    if (!deleting?.cafe) return;
    setDeleteWorking(true);
    setDeleteError(null);
    try {
      await axios.delete(`${API}/admin/cafes/${deleting.cafe.slug}`, { headers: { "X-Admin-Key": adminKey } });
      setDeleting(null);
      setDeleteTyped("");
      await reload();
    } catch (err) {
      setDeleteError(err.response?.data?.detail || err.message || "Could not delete");
    } finally {
      setDeleteWorking(false);
    }
  };

  const cancelDelete = () => {
    if (deleteWorking) return;
    setDeleting(null);
    setDeleteTyped("");
    setDeleteError(null);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    setJustCreated(null);
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim().toLowerCase(),
        email: form.email.trim().toLowerCase(),
        custom_note: form.custom_note.trim() || null,
        payout_method: form.payout_method,
        partner_type: form.partner_type,
      };
      if (form.payout_handle.trim()) payload.payout_handle = form.payout_handle.trim();
      const res = await axios.post(`${API}/admin/cafes`, payload, {
        headers: { "X-Admin-Key": adminKey },
      });
      setJustCreated(res.data);
      setForm({ name: "", slug: "", email: "", custom_note: "", payout_method: "venmo", payout_handle: "", partner_type: "cafe" });
      await reload();
    } catch (err) {
      setCreateError(err.response?.data?.detail || "Could not create cafe.");
    } finally {
      setCreating(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    tryAuth(keyInput.trim());
  };

  const copyText = (t) => {
    try { navigator.clipboard?.writeText(t); } catch (_e) { /* ignore */ }
  };

  if (authed === null) {
    return <div style={pageStyle}><p style={{ color: "var(--cream-soft)", padding: 40, textAlign: "center" }}>Checking…</p></div>;
  }

  if (!authed) {
    return (
      <div style={pageStyle}>
        <div style={{ maxWidth: 420, margin: "60px auto", padding: 28, border: "1px solid rgba(143, 196, 224, 0.32)", background: "rgba(143, 196, 224, 0.04)" }}>
          <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "var(--terracotta-soft)", margin: 0 }}>
            // soma · admin
          </p>
          <h1 style={{ fontFamily: "'IM Fell DW Pica', serif", fontSize: 26, fontStyle: "italic", color: "var(--cream)", margin: "10px 0 20px" }}>
            Cafe onboarding
          </h1>
          <form onSubmit={handleLogin}>
            <input
              data-testid="admin-key-input"
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="admin key"
              autoFocus
              style={inputStyle}
            />
            {error && <p style={{ color: "var(--rust)", fontSize: 13, margin: "10px 0 0" }} data-testid="admin-error">{error}</p>}
            <button type="submit" data-testid="admin-submit" style={{ ...btnStyle, marginTop: 14 }}>Enter</button>
          </form>
          <p style={{ marginTop: 18, fontSize: 12, color: "var(--dim)" }}>
            For Soma Lixir staff only. <Link to="/" style={{ color: "var(--terracotta-soft)" }}>← back home</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle} data-testid="admin-cafes-page">
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "28px 18px 60px" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div>
            <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "var(--terracotta-soft)", margin: 0 }}>
              // soma · admin · cafes
            </p>
            <h1 style={{ fontFamily: "'IM Fell DW Pica', serif", fontSize: 30, fontStyle: "italic", color: "var(--cream)", margin: "6px 0 0" }}>
              Cafe onboarding
            </h1>
            <p style={{ color: "var(--dim)", fontSize: 13, margin: "4px 0 0" }}>
              {cafes.length} cafe{cafes.length === 1 ? "" : "s"} onboarded
            </p>
          </div>
          <nav style={{ display: "flex", gap: 12, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase" }}>
            <Link to="/admin/feedback" style={navLinkStyle}>feedback →</Link>
          </nav>
        </div>

        {/* /begin recruitment funnel */}
        {beginStats && (
          <section data-testid="begin-funnel-stats" style={{ ...cardStyle, marginBottom: 24 }}>
            <h2 style={sectionTitleStyle}>/begin recruitment funnel</h2>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginTop: 8 }}>
              <div>
                <p data-testid="begin-views" style={{ fontFamily: "'IM Fell DW Pica', serif", fontSize: 32, color: "var(--cream)", margin: 0 }}>{beginStats.views}</p>
                <p style={{ color: "var(--dim)", fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", margin: "2px 0 0", fontFamily: "'Space Mono', monospace" }}>page views</p>
              </div>
              <div>
                <p data-testid="begin-clicks" style={{ fontFamily: "'IM Fell DW Pica', serif", fontSize: 32, color: "var(--cream)", margin: 0 }}>{beginStats.apply_clicks}</p>
                <p style={{ color: "var(--dim)", fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", margin: "2px 0 0", fontFamily: "'Space Mono', monospace" }}>apply clicks</p>
              </div>
              <div>
                <p data-testid="begin-rate" style={{ fontFamily: "'IM Fell DW Pica', serif", fontSize: 32, color: "var(--gold)", margin: 0 }}>{beginStats.conversion_rate}%</p>
                <p style={{ color: "var(--dim)", fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", margin: "2px 0 0", fontFamily: "'Space Mono', monospace" }}>click-through</p>
              </div>
            </div>
          </section>
        )}

        {/* CREATE FORM */}
        <section style={cardStyle} data-testid="admin-create-cafe-card">
          <h2 style={sectionTitleStyle}>+ create a new cafe</h2>
          <p style={{ color: "var(--cream-soft)", fontStyle: "italic", fontSize: 13, margin: "0 0 16px", fontFamily: "'IM Fell DW Pica', serif" }}>
            For in-person conversations — set them up live, hand them the temp password.
          </p>

          {justCreated && (
            <div data-testid="just-created-banner" style={successCardStyle}>
              <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: "var(--terracotta-soft)", margin: "0 0 8px" }}>
                // saved · share these out loud
              </p>
              <p style={{ fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", color: "var(--cream)", fontSize: 16, margin: "0 0 14px" }}>
                <strong>{justCreated.cafe.name}</strong> is live.
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
                <CredRow label="landing URL" value={`${window.location.origin}/c/${justCreated.cafe.slug}`} onCopy={copyText} />
                <CredRow label="login URL"   value={`${window.location.origin}/cafe/login`} onCopy={copyText} />
                <CredRow label="email"       value={justCreated.cafe.email} onCopy={copyText} />
                <CredRow label="temp password" value={justCreated.temp_password} onCopy={copyText} highlight />
              </ul>
              <p style={{ fontSize: 11, color: "var(--dim)", marginTop: 12, fontStyle: "italic", fontFamily: "'IM Fell DW Pica', serif" }}>
                The partner can change their password and add their payout handle after first login. This is the only place the temp password is shown — copy it now.
              </p>

              {/* QR code + live iframe preview, side-by-side on wide screens */}
              <div style={{ marginTop: 22, display: "grid", gap: 18, gridTemplateColumns: "minmax(0, 1fr)" }}>
                <div style={{ display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
                  <div>
                    <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: "var(--terracotta-soft)", margin: "0 0 10px" }}>
                      // table-tent
                    </p>
                    {/* mode toggle */}
                    <div style={{ display: "flex", gap: 4, marginBottom: 10 }}>
                      <button type="button" data-testid="admin-pdf-mode-light"
                        onClick={() => setPdfLight(true)}
                        style={miniToggleBtn(pdfLight)}>light</button>
                      <button type="button" data-testid="admin-pdf-mode-dark"
                        onClick={() => setPdfLight(false)}
                        style={miniToggleBtn(!pdfLight)}>dark</button>
                    </div>
                    <CafeQR
                      url={`${window.location.origin}/c/${justCreated.cafe.slug}`}
                      size={170}
                      filename={`soma-lixir-${justCreated.cafe.slug}-qr.png`}
                      mode={pdfLight ? "light" : "dark"}
                      testId="just-created-qr"
                      showDownloadBtn={false}
                    />
                    {/* variant picker (compact) */}
                    <select
                      data-testid="admin-pdf-variant"
                      value={pdfVariant}
                      onChange={(e) => setPdfVariant(e.target.value)}
                      style={{ marginTop: 10, width: "100%", padding: "8px 10px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(143,196,224,0.32)", color: "var(--cream)", fontFamily: "'Space Mono', monospace", fontSize: 11, borderRadius: 0 }}
                    >
                      <option value="classic">classic · direct</option>
                      <option value="poetic">poetic · pull-quote</option>
                      <option value="close">close · invitation</option>
                    </select>
                    <button
                      type="button"
                      data-testid="just-created-tent-pdf-btn"
                      onClick={async () => {
                        try {
                          await generateTableTentPDF({
                            cafe: justCreated.cafe,
                            qrCanvasSelector: '[data-testid="just-created-qr"]',
                            landingOrigin: window.location.origin,
                            variant: pdfVariant,
                            lightMode: pdfLight,
                          });
                        } catch (err) {
                          console.error("PDF failed:", err);
                          alert("Couldn't generate the PDF. Try again in a second.");
                        }
                      }}
                      style={{
                        marginTop: 8,
                        background: "transparent",
                        border: "1px solid var(--terracotta-soft)",
                        color: "var(--terracotta-soft)",
                        padding: "8px 14px",
                        fontFamily: "'Space Mono', monospace",
                        fontSize: 9,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        cursor: "pointer",
                        borderRadius: 0,
                        width: "100%",
                      }}
                    >
                      ↓ download table-tent PDF
                    </button>
                  </div>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: "var(--terracotta-soft)", margin: "0 0 10px" }}>
                      // live preview · what they'll see
                    </p>
                    <div style={{ position: "relative", width: "100%", paddingTop: "150%", border: "1px solid rgba(143, 196, 224, 0.32)", overflow: "hidden", background: "#050B1B" }}>
                      <iframe
                        title={`Preview of ${justCreated.cafe.name}`}
                        data-testid="just-created-preview"
                        src={`/c/${justCreated.cafe.slug}`}
                        style={{ position: "absolute", top: 0, left: 0, width: "117.6%", height: "117.6%", border: "none", transform: "scale(0.85)", transformOrigin: "top left" }}
                      />
                    </div>
                    <a
                      href={`/c/${justCreated.cafe.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: "inline-block", marginTop: 8, ...openLinkStyle }}
                    >
                      open in new tab →
                    </a>
                  </div>
                </div>
              </div>

              <button
                data-testid="dismiss-just-created"
                onClick={() => setJustCreated(null)}
                style={{ ...ghostBtnStyle, marginTop: 14 }}
              >
                done — clear & set up another
              </button>
            </div>
          )}

          {!justCreated && (
            <form onSubmit={handleCreate}>
              <Field label="Partner type *">
                <div style={{ display: "flex", gap: 8 }} data-testid="cf-partner-type">
                  {[
                    { v: "cafe", label: "Cafe / Trailer" },
                    { v: "creator", label: "Creator / Individual" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`cf-partner-type-${opt.v}`}
                      onClick={() => setForm({ ...form, partner_type: opt.v })}
                      className={`mono-btn ${form.partner_type === opt.v ? "active" : ""}`}
                      style={{ flex: 1 }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </Field>
              <Row>
                <Field label={form.partner_type === "creator" ? "Creator name / handle *" : "Cafe name *"} htmlFor="cf-name">
                  <input id="cf-name" data-testid="cf-name" required value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={form.partner_type === "creator" ? "Jessica · @jessreads" : "Morning Pearl Roasters"} style={inputStyle} />
                </Field>
                <Field label="Slug * (becomes /c/__)" htmlFor="cf-slug">
                  <input id="cf-slug" data-testid="cf-slug" required value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                    placeholder={form.partner_type === "creator" ? "jessreads" : "morning-pearl"} style={inputStyle} pattern="[a-z0-9][a-z0-9-]{1,38}[a-z0-9]" minLength={3} maxLength={40} />
                </Field>
              </Row>
              <Field label="Owner email *" htmlFor="cf-email">
                <input id="cf-email" data-testid="cf-email" required type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="owner@morningpearl.com" style={inputStyle} />
              </Field>
              <Field label="Welcome note (optional · shown on their landing card)" htmlFor="cf-note">
                <textarea id="cf-note" data-testid="cf-note" value={form.custom_note}
                  onChange={(e) => setForm({ ...form, custom_note: e.target.value })}
                  rows={2} maxLength={160}
                  placeholder='"From the team at Morning Pearl — enjoy a moment with us." (160 chars max)'
                  style={{ ...inputStyle, fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic" }} />
              </Field>
              <Row>
                <Field label="Payout method" htmlFor="cf-method">
                  <select id="cf-method" data-testid="cf-method" value={form.payout_method}
                    onChange={(e) => setForm({ ...form, payout_method: e.target.value })}
                    style={inputStyle}>
                    <option value="venmo">Venmo</option>
                    <option value="stripe">Stripe</option>
                  </select>
                </Field>
                <Field label="Payout handle (optional · they can fill in later)" htmlFor="cf-handle">
                  <input id="cf-handle" data-testid="cf-handle" value={form.payout_handle}
                    onChange={(e) => setForm({ ...form, payout_handle: e.target.value })}
                    placeholder="@morningpearl" style={inputStyle} />
                </Field>
              </Row>
              {createError && <p data-testid="create-error" style={{ color: "var(--rust)", fontSize: 13, margin: "12px 0 0" }}>{createError}</p>}
              <button type="submit" data-testid="cf-submit" disabled={creating} style={{ ...btnStyle, marginTop: 16, opacity: creating ? 0.6 : 1 }}>
                {creating ? "Creating…" : "Create & generate temp password"}
              </button>
            </form>
          )}
        </section>

        {/* CAFE LIST */}
        <section style={cardStyle} data-testid="admin-cafe-list">
          <h2 style={sectionTitleStyle}>onboarded cafes</h2>
          {cafes.length === 0 ? (
            <p style={{ color: "var(--dim)", fontStyle: "italic" }}>No cafes yet. Add one above.</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 12 }}>
              {cafes.map((c) => (
                <li key={c.id} data-testid={`cafe-row-${c.slug}`} style={cafeRowStyle}>
                  <div>
                    <p style={{ margin: 0, fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 18, color: "var(--cream)" }}>{c.name}</p>
                    <p style={{ margin: "2px 0 0", fontFamily: "'Space Mono', monospace", fontSize: 11, color: "var(--cream-soft)" }}>
                      /c/{c.slug} · {c.email}
                    </p>
                    <p style={{ margin: "2px 0 0", fontFamily: "'Space Mono', monospace", fontSize: 10, color: "var(--dim)" }}>
                      {c.payout_method} · {c.payout_handle || "(no handle yet)"}
                      {c.must_change_password && <span style={{ color: "var(--gold)", marginLeft: 8 }}>· temp password not yet changed</span>}
                    </p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                    <a href={`/c/${c.slug}`} target="_blank" rel="noopener noreferrer" style={openLinkStyle} data-testid={`cafe-open-${c.slug}`}>
                      open →
                    </a>
                    <button
                      type="button"
                      data-testid={`cafe-delete-${c.slug}`}
                      onClick={() => openDelete(c)}
                      title={`Delete ${c.name} and all their data`}
                      style={{
                        background: "transparent",
                        border: "1px solid rgba(207, 102, 92, 0.45)",
                        color: "var(--rust)",
                        fontFamily: "'Space Mono', monospace",
                        fontSize: 10,
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        padding: "6px 10px",
                        cursor: "pointer",
                      }}
                    >
                      delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {deleting && (
        <DeleteCafeModal
          deleting={deleting}
          typed={deleteTyped}
          setTyped={setDeleteTyped}
          working={deleteWorking}
          error={deleteError}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
}

// -------- delete confirm modal --------
function DeleteCafeModal({ deleting, typed, setTyped, working, error, onConfirm, onCancel }) {
  const { cafe, footprint } = deleting;
  const slug = cafe.slug;
  const canDelete = typed.trim().toLowerCase() === slug && !working;
  return (
    <div
      data-testid="delete-cafe-modal"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
      style={{
        position: "fixed", inset: 0, background: "rgba(8, 18, 35, 0.78)",
        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9999, padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-1)",
          border: "1px solid var(--rust)",
          padding: "26px 24px 22px",
          maxWidth: 460,
          width: "100%",
          fontFamily: "'Space Mono', monospace",
          color: "var(--cream)",
        }}
      >
        <p style={{ margin: 0, fontFamily: "'IM Fell DW Pica', serif", fontStyle: "italic", fontSize: 22, color: "var(--rust)" }}>
          delete this {cafe.partner_type || "cafe"}?
        </p>
        <p style={{ margin: "12px 0 14px", fontSize: 13, lineHeight: 1.5, color: "var(--cream-soft)" }}>
          You&apos;re about to permanently remove <strong style={{ color: "var(--cream)" }}>{cafe.name}</strong> ({cafe.email}) and everything linked to <strong style={{ color: "var(--cream)" }}>/c/{slug}</strong>.
        </p>
        {footprint ? (
          <ul data-testid="delete-cafe-footprint" style={{ listStyle: "none", margin: "0 0 18px", padding: "12px 14px", background: "rgba(207, 102, 92, 0.08)", border: "1px solid rgba(207, 102, 92, 0.3)", fontSize: 12, lineHeight: 1.7 }}>
            <li>· {footprint.reflections} reading{footprint.reflections === 1 ? "" : "s"}</li>
            <li>· {footprint.cafe_events} tracking event{footprint.cafe_events === 1 ? "" : "s"}</li>
            <li>· {footprint.payment_transactions} payment record{footprint.payment_transactions === 1 ? "" : "s"}</li>
            <li>· the {cafe.partner_type || "cafe"} account itself</li>
          </ul>
        ) : (
          <p style={{ fontSize: 12, color: "var(--gold)", marginBottom: 18, fontStyle: "italic" }}>
            (Couldn&apos;t preview footprint — delete will still work, but data counts won&apos;t be shown.)
          </p>
        )}
        <p style={{ margin: "0 0 8px", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--terracotta-soft)" }}>
          To confirm, type the slug below:
        </p>
        <input
          data-testid="delete-cafe-confirm-input"
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={slug}
          style={{
            width: "100%",
            padding: "10px 12px",
            background: "rgba(143, 196, 224, 0.05)",
            border: `1px solid ${canDelete ? "var(--rust)" : "rgba(143, 196, 224, 0.25)"}`,
            color: "var(--cream)",
            fontFamily: "'Space Mono', monospace",
            fontSize: 14,
            letterSpacing: "0.05em",
            marginBottom: 16,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        {error && <p data-testid="delete-cafe-error" style={{ color: "var(--rust)", fontSize: 12, marginBottom: 12 }}>{error}</p>}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            type="button"
            data-testid="delete-cafe-cancel"
            onClick={onCancel}
            disabled={working}
            style={{
              background: "transparent",
              border: "1px solid rgba(143, 196, 224, 0.35)",
              color: "var(--cream-soft)",
              fontFamily: "'Space Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              padding: "9px 14px",
              cursor: working ? "not-allowed" : "pointer",
            }}
          >
            cancel
          </button>
          <button
            type="button"
            data-testid="delete-cafe-confirm"
            onClick={onConfirm}
            disabled={!canDelete}
            style={{
              background: canDelete ? "var(--rust)" : "rgba(207, 102, 92, 0.25)",
              border: "1px solid var(--rust)",
              color: canDelete ? "var(--cream)" : "rgba(255,255,255,0.5)",
              fontFamily: "'Space Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              padding: "9px 14px",
              cursor: canDelete ? "pointer" : "not-allowed",
            }}
          >
            {working ? "deleting…" : "delete forever"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -------- small UI helpers --------
function Field({ label, htmlFor, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label htmlFor={htmlFor} style={{ display: "block", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--terracotta-soft)", marginBottom: 6 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
function Row({ children }) {
  return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>{children}</div>;
}
function CredRow({ label, value, onCopy, highlight }) {
  return (
    <li style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 10px", background: highlight ? "rgba(212, 200, 150, 0.10)" : "rgba(143, 196, 224, 0.05)", border: `1px solid ${highlight ? "var(--gold)" : "rgba(143, 196, 224, 0.22)"}` }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <p style={{ margin: 0, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: "0.22em", textTransform: "uppercase", color: highlight ? "var(--gold)" : "var(--terracotta-soft)" }}>{label}</p>
        <p style={{ margin: "2px 0 0", fontFamily: "'Space Mono', monospace", fontSize: 13, color: "var(--cream)", overflowWrap: "anywhere" }}>{value}</p>
      </div>
      <button type="button" onClick={() => onCopy(value)} style={copyBtnStyle} data-testid={`copy-${label.replace(/\s+/g,'-')}`}>copy</button>
    </li>
  );
}

// -------- styles --------
const pageStyle = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #152340 0%, #0B142A 50%, #050B1B 100%)",
  color: "var(--cream)",
  fontFamily: "'Space Mono', monospace",
};
const cardStyle = {
  marginBottom: 28,
  padding: 22,
  border: "1px solid rgba(143, 196, 224, 0.28)",
  background: "rgba(143, 196, 224, 0.04)",
};
const successCardStyle = {
  padding: 18,
  border: "1px solid var(--terracotta-soft)",
  background: "rgba(143, 196, 224, 0.10)",
  marginBottom: 4,
};
const sectionTitleStyle = {
  fontFamily: "'IM Fell DW Pica', serif",
  fontStyle: "italic",
  fontSize: 22,
  color: "var(--cream)",
  margin: "0 0 6px",
};
const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  background: "rgba(255, 255, 255, 0.03)",
  border: "1px solid rgba(143, 196, 224, 0.32)",
  color: "var(--cream)",
  fontFamily: "'Space Mono', monospace",
  fontSize: 13,
  borderRadius: 0,
  outline: "none",
  boxSizing: "border-box",
};
const btnStyle = {
  width: "100%",
  padding: "12px 16px",
  background: "var(--terracotta-soft)",
  border: "none",
  color: "var(--bg-1)",
  fontFamily: "'Space Mono', monospace",
  fontSize: 11,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  cursor: "pointer",
  borderRadius: 0,
};
const ghostBtnStyle = {
  ...btnStyle,
  background: "transparent",
  border: "1px solid var(--terracotta-soft)",
  color: "var(--terracotta-soft)",
};
const copyBtnStyle = {
  background: "transparent",
  border: "1px solid var(--terracotta-soft)",
  color: "var(--terracotta-soft)",
  padding: "6px 10px",
  fontFamily: "'Space Mono', monospace",
  fontSize: 9,
  letterSpacing: "0.22em",
  textTransform: "uppercase",
  cursor: "pointer",
  borderRadius: 0,
};
const cafeRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  padding: "12px 14px",
  background: "rgba(143, 196, 224, 0.04)",
  border: "1px solid rgba(143, 196, 224, 0.18)",
};
const openLinkStyle = {
  color: "var(--terracotta-soft)",
  textDecoration: "none",
  fontFamily: "'Space Mono', monospace",
  fontSize: 10,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  borderBottom: "1px dotted var(--terracotta-soft)",
  paddingBottom: 1,
  whiteSpace: "nowrap",
};
const navLinkStyle = {
  color: "var(--terracotta-soft)",
  textDecoration: "none",
  borderBottom: "1px dotted var(--terracotta-soft)",
  paddingBottom: 1,
};
const miniToggleBtn = (active) => ({
  flex: 1,
  padding: "6px 8px",
  background: active ? "var(--terracotta-soft)" : "transparent",
  border: "1px solid var(--terracotta-soft)",
  color: active ? "var(--bg-1)" : "var(--terracotta-soft)",
  fontFamily: "'Space Mono', monospace",
  fontSize: 9,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  cursor: "pointer",
  borderRadius: 0,
});
